import React from "react";
import HomeEventSwiper from "@components/swiper/HomeEventSwiper";

import styled from "@emotion/styled";
import { SwiperSlide } from "swiper/react";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useGenderStore from "@store/header/useGenderStore";
import { useNavigate } from "react-router-dom";

const getOSType = (): "Android" | "iOS" | "unknown" => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.indexOf("android") > -1) return "Android";
  if (
    userAgent.indexOf("iphone") > -1 ||
    userAgent.indexOf("ipad") > -1 ||
    userAgent.indexOf("ipod") > -1
  )
    return "iOS";

  return "unknown";
};

const SwiperItem = styled(SwiperSlide)<{ background: "string" }>(
  ({ background }) =>
    ({
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
      background: `url(${background}) no-repeat center/cover`,
      padding: "3rem",
      minHeight: "45rem",
    }) as const,
);

const TitleWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  color: theme.color.mint,
  ".title": {
    fontSize: "2.8rem",
    lineHeight: "4.4rem",
    fontWeight: theme.fontWeight.medium,
  },
  ".brand": {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
    fontWeight: theme.fontWeight.regular,
  },
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  position: "absolute",
  bottom: "4.4rem",
  right: "2rem",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: theme.color.mint,
  fontSize: "2.4rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "2.4rem",
  cursor: "pointer",
}));

const EventSwiper = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getPromotionList } = useApiRequest();
  const { gender } = useGenderStore();
  const lang = window.localStorage.getItem("language");

  const categoryId = gender === "men" ? 1002 : 1001;

  const { data: promotionList, isLoading } = useQuery({
    queryKey: ["promotionList", gender],
    queryFn: () => getPromotionList(categoryId),
  });

  const handleNavigate = (
    type: string,
    brandId: null | number,
    sellerId: null | number,
    eventId: null | number,
    link: string | null,
  ) => {
    const os = getOSType();

    if (type === "E" && eventId) {
      navigate(`/event/${eventId}`);
    }
    if (type === "B" && brandId) {
      navigate(`/brand/${brandId}`);
    }
    if (type === "S" && sellerId) {
      navigate(`/seller/${sellerId}`);
    }
    if (type === "L" && link) {
      if (os === "Android") {
        window.android.openBrowser(link);
      } else if (os === "iOS") {
        window.webkit.messageHandlers.openBrowser.postMessage(link);
      } else {
        window.open(link, "_blank");
      }
    }
  };

  return (
    <HomeEventSwiper>
      {promotionList?.map((item: any, index: number) => (
        <SwiperItem key={index} background={item.thumbnail_url}>
          <TitleWrapper>
            <span className="title">
              {lang === "en" && item.title_en ? item.title_en : item.title_ko}
            </span>
            {item.promotion_type === "E" && (
              <span className="brand">
                {(lang === "en" && item?.introduction_en
                  ? item?.introduction_en
                  : item?.introduction_ko
                )
                  ?.split("\n")
                  .map((line: any, index: number, arr: string[]) => (
                    <React.Fragment key={index}>
                      {line}
                      {index !== arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
              </span>
            )}
            {item.promotion_type === "B" && (
              <span className="brand">
                {lang === "en" ? item.brand_name_en : item.brand_name_ko}
              </span>
            )}

            {item.promotion_type === "S" && (
              <span className="brand">{item.seller_name}</span>
            )}
          </TitleWrapper>

          <ButtonWrapper
            onClick={() =>
              handleNavigate(
                item.promotion_type,
                item.brand_id,
                item.seller_id,
                item.promotion_id,
                item.link,
              )
            }>
            <span>GO</span>
            <Image src={theme.image.arrow_right_mint.default} size="2.4rem" />
          </ButtonWrapper>
        </SwiperItem>
      ))}
    </HomeEventSwiper>
  );
};

export default EventSwiper;
